(function (root) {
  'use strict';

  const lensHeadsetMap = {
    'Quest 3': 'Meta Quest 3',
    'Quest 3S': 'Meta Quest 3S/2',
    'Quest 2': 'Meta Quest 3S/2',
    'Pico 4': 'Pico 4',
  };

  function normalizeLensHeadset(headset) {
    return lensHeadsetMap[headset] || headset || '';
  }

  function hasMatchingLens(headset, lensHeadsets) {
    return lensHeadsets.includes(normalizeLensHeadset(headset));
  }

  function deriveAssistantState(state) {
    const lensDetected = hasMatchingLens(state.headset, state.lensHeadsets);
    const visionResolved = lensDetected || state.myopia === 'no' || state.visionPlan === 'contacts';

    return {
      lensDetected,
      complete: visionResolved && Boolean(state.battery) && Boolean(state.streaming),
      showLensRecommendation: !lensDetected && state.myopia === 'yes' && state.visionPlan !== 'contacts',
      showCableRecommendation: state.battery === 'yes' && !state.hasCable,
      showCableDetected: state.battery === 'yes' && state.hasCable,
      showWiredHubRecommendation: ['wired', 'both'].includes(state.streaming),
    };
  }

  const api = { normalizeLensHeadset, hasMatchingLens, deriveAssistantState };

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }

  if (!root || !root.customElements || root.customElements.get('pt-device-purchase-assistant')) return;

  class PTDevicePurchaseAssistant extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.config = JSON.parse(this.querySelector('[data-pt-device-config]').textContent);
      this.form = this.closest('form');
      this.productInfo = this.closest('product-info') || this.closest('.product__info-container') || document;
      this.submitButton = this.form && this.form.querySelector('[name="add"]');
      this.state = {
        headset: this.readHeadset(),
        lensHeadsets: this.config.lensHeadsets || [],
        hasCable: Boolean(this.config.hasCable),
        myopia: '',
        visionPlan: '',
        battery: '',
        streaming: '',
      };

      if (['localhost', '127.0.0.1'].includes(root.location.hostname)) {
        const demo = new URLSearchParams(root.location.search).get('pt_assistant_demo');
        if (demo === 'empty') {
          this.state.lensHeadsets = [];
          this.state.hasCable = false;
        } else if (demo === 'configured') {
          this.state.lensHeadsets = [normalizeLensHeadset(this.state.headset)];
          this.state.hasCable = true;
        }
      }

      this.bindEvents();
      this.restoreState();
      this.render();
    }

    bindEvents() {
      this.querySelectorAll('[data-pt-group] button[data-pt-value]').forEach((button) => {
        button.addEventListener('click', () => {
          const group = button.closest('[data-pt-group]').dataset.ptGroup;
          this.state[group] = button.dataset.ptValue;
          if (group === 'myopia') this.state.visionPlan = '';
          this.saveState();
          this.render();
        });
      });

      this.querySelector('[data-pt-use-contacts]').addEventListener('click', () => {
        this.state.visionPlan = 'contacts';
        this.saveState();
        this.render();
      });

      const variantSelects = this.productInfo.querySelector('variant-selects');
      if (variantSelects) {
        variantSelects.addEventListener('change', () => {
          this.state.headset = this.readHeadset();
          this.saveState();
          this.render();
        });
      }

      if (this.form) {
        this.form.addEventListener(
          'submit',
          (event) => {
            if (deriveAssistantState(this.state).complete) return;
            event.preventDefault();
            event.stopImmediatePropagation();
            this.querySelector('[data-pt-error]').hidden = false;
            this.scrollIntoView({ behavior: 'smooth', block: 'center' });
          },
          true,
        );
      }
    }

    storageKey() {
      return `pt-device-assistant:${this.dataset.productFormId || 'product'}`;
    }

    saveState() {
      try {
        root.sessionStorage.setItem(
          this.storageKey(),
          JSON.stringify({
            headset: this.state.headset,
            myopia: this.state.myopia,
            visionPlan: this.state.visionPlan,
            battery: this.state.battery,
            streaming: this.state.streaming,
          }),
        );
      } catch (error) {
        // Storage is an enhancement only; checkout validation does not depend on it.
      }
    }

    restoreState() {
      try {
        const saved = JSON.parse(root.sessionStorage.getItem(this.storageKey()) || 'null');
        if (!saved || saved.headset !== this.state.headset) return;
        this.state.myopia = saved.myopia || '';
        this.state.visionPlan = saved.visionPlan || '';
        this.state.battery = saved.battery || '';
        this.state.streaming = saved.streaming || '';
      } catch (error) {
        // Ignore unavailable or malformed session storage.
      }
    }

    readHeadset() {
      const checked = this.productInfo.querySelector('variant-selects input[type="radio"]:checked');
      if (checked) return checked.value;

      const select = this.productInfo.querySelector('variant-selects select');
      if (select) return select.value;

      return '';
    }

    toggle(selector, show) {
      this.querySelector(selector).hidden = !show;
    }

    updateGroup(group, value) {
      this.querySelectorAll(`[data-pt-group="${group}"] button[data-pt-value]`).forEach((button) => {
        const selected = button.dataset.ptValue === value;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', String(selected));
      });
    }

    updateOutputs(derived) {
      const vision = derived.lensDetected
        ? 'lens_in_cart'
        : this.state.myopia === 'no'
          ? 'none'
          : this.state.visionPlan === 'contacts'
            ? 'contacts'
            : this.state.myopia === 'yes'
              ? 'lens_recommended'
              : '';

      this.querySelector('[data-pt-output="headset"]').value = this.state.headset;
      this.querySelector('[data-pt-output="vision"]').value = vision;
      this.querySelector('[data-pt-output="battery"]').value = this.state.battery;
      this.querySelector('[data-pt-output="streaming"]').value = this.state.streaming;
    }

    updateSubmitButton(complete) {
      if (!this.submitButton || this.submitButton.disabled) return;

      if (!complete) {
        this.submitButton.dataset.ptAssistantDisabled = 'true';
        this.submitButton.setAttribute('aria-disabled', 'true');
      } else if (this.submitButton.dataset.ptAssistantDisabled === 'true') {
        delete this.submitButton.dataset.ptAssistantDisabled;
        this.submitButton.removeAttribute('aria-disabled');
      }
    }

    render() {
      const derived = deriveAssistantState(this.state);

      this.toggle('[data-pt-lens-detected]', derived.lensDetected);
      this.toggle('[data-pt-vision-question]', !derived.lensDetected);
      this.toggle('[data-pt-lens-recommendation]', derived.showLensRecommendation);
      this.toggle('[data-pt-contacts-confirmed]', this.state.visionPlan === 'contacts' && !derived.lensDetected);
      this.toggle('[data-pt-cable-recommendation]', derived.showCableRecommendation);
      this.toggle('[data-pt-cable-detected]', derived.showCableDetected);
      this.toggle('[data-pt-wired-hub-recommendation]', derived.showWiredHubRecommendation);
      this.toggle('[data-pt-complete]', derived.complete);

      const streamingHint = this.config.streamingHints[this.state.streaming] || '';
      const hintElement = this.querySelector('[data-pt-streaming-hint]');
      hintElement.textContent = streamingHint;
      hintElement.hidden = !streamingHint;

      this.querySelector('[data-pt-error]').hidden = true;
      this.updateGroup('myopia', this.state.myopia);
      this.updateGroup('battery', this.state.battery);
      this.updateGroup('streaming', this.state.streaming);
      this.updateOutputs(derived);
      this.updateSubmitButton(derived.complete);

      const lensLink = this.querySelector('[data-pt-lens-link]');
      const lensBaseUrl = lensLink.href.split(/[?&]pt_headset=/)[0];
      const separator = lensBaseUrl.includes('?') ? '&' : '?';
      lensLink.href = `${lensBaseUrl}${separator}pt_headset=${encodeURIComponent(
        normalizeLensHeadset(this.state.headset),
      )}`;
    }
  }

  root.customElements.define('pt-device-purchase-assistant', PTDevicePurchaseAssistant);
})(typeof window !== 'undefined' ? window : null);
